import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Divider, FormGroup, Grid, Typography } from '@material-ui/core';
import { darken, makeStyles } from '@material-ui/core/styles';
import { useFormikContext } from 'formik';
import classNames from 'classnames';

import {
  ManualNotificationsItemUpdateInput,
  ManualNotificationType,
  UserSegmentFragment,
} from 'api';
import { Alert, Loaders, PaddedPaper, ValidatedTextField } from 'components';
import { useProtectedClient } from 'hooks';

import SegmentSelect from './SegmentSelect';
import Schedule from './Schedule';
import EmailInput from './EmailInput';

interface IProps {
  titleI18nKey: string;
  segments: UserSegmentFragment[];
  onCancel?(): void;
  onDelete?(id: string): void;
}

const MAX_MESSAGE_LENGTH = 140;

const useStyles = makeStyles(theme => ({
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  primaryPadding: {
    padding: `${theme.spacing(2)}px ${theme.spacing(8)}px ${theme.spacing(1)}px`,
  },
  formGroup: {
    marginTop: theme.spacing(2),
  },
  options: {
    marginTop: theme.spacing(3),
  },
  safe: {
    color: theme.custom.palette.grey1,
  },
  warning: {
    color: theme.custom.palette.orange,
  },
  error: {
    color: theme.custom.palette.red,
  },
  deleteButton: {
    marginLeft: theme.spacing(1),
    borderColor: theme.palette.error.main,
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: darken(theme.palette.error.main, 0.1),
      color: theme.palette.common.white,
    },
  },
}));

const Form: FC<IProps> = ({ segments, titleI18nKey, onDelete, onCancel }) => {
  const { title } = useProtectedClient();
  const { t } = useTranslation();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const classes = useStyles();
  const { values, isSubmitting } = useFormikContext<ManualNotificationsItemUpdateInput>();

  const { message, notification_type } = values;
  const isScheduled = notification_type === ManualNotificationType.Scheduled;
  const i18nKey = isScheduled ? 'common:schedule' : 'common:send';

  function renderCounter() {
    const { body } = message;
    const difference = MAX_MESSAGE_LENGTH - body.length;
    const className = classNames({
      [classes.safe]: difference >= 21,
      [classes.warning]: difference <= 20,
      [classes.error]: difference < 0,
    });

    return (
      <Typography className={className}>{`${
        body ? body.length : 0
      }/${MAX_MESSAGE_LENGTH}`}</Typography>
    );
  }

  return (
    <PaddedPaper>
      <Grid container alignItems="center" justify="space-between">
        <Grid item>
          <Typography variant="subtitle2">{t(titleI18nKey, { title })}</Typography>
        </Grid>
        <Grid item>{renderCounter()}</Grid>
      </Grid>
      <Divider className={classes.divider} />
      <SegmentSelect segments={segments} />
      <div className={classes.primaryPadding}>
        <ValidatedTextField
          persistLabel
          name="message.body"
          i18nKey="messages:addCustomNotification"
        />
        <ValidatedTextField persistLabel name="message.url" i18nKey="messages:addOptionalUrl" />
        <FormGroup className={classes.formGroup}>
          <Schedule />
          <EmailInput />
        </FormGroup>
      </div>
      <Divider className={classes.divider} />
      <Grid container justify="space-between">
        <Grid item>
          {onDelete && (
            <Button
              size="large"
              variant="outlined"
              className={classes.deleteButton}
              onClick={() => setDeleteOpen(true)}
            >
              {t('common:delete')}
            </Button>
          )}
        </Grid>
        <Grid item>
          <Grid container spacing={2}>
            <Grid item>
              {onCancel && (
                <Button size="large" variant="outlined" onClick={onCancel}>
                  {t('common:cancel')}
                </Button>
              )}
            </Grid>
            <Grid item>
              <Button color="primary" size="large" type="submit" variant="contained">
                {isSubmitting ? <Loaders.Standard height={26} /> : t(i18nKey)}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Alert
        open={deleteOpen}
        onConfirm={() => {
          onDelete(values.id);
          setDeleteOpen(false);
        }}
        onCancel={() => setDeleteOpen(false)}
        message={t('messages:deleteManualNotificationMessage')}
        confirm={t('common:delete')}
      />
    </PaddedPaper>
  );
};

export default Form;
